import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import MediaButton from '../Buttons/MediaButton/MediaButton';
import { ButtonStyleType, MediaButtonType } from '../../types/button.types';
import { VideoCardData } from '../../types/video-card.types';
import {
    formatVideoCardDate,
    formatVideoCardFightNumber,
    formatVideoCardParticipantName,
    orderVideoCardParticipants
} from '../../utils/video-card';
import FighterCornerBars from '../FighterCornerBars/FighterCornerBars';
import { usePurchaseStatus } from '../../providers/purchase-status.provider';
import { usePurchaseFlow } from '../../hooks/usePurchaseFlow';
import { Routes } from '../../types/constants';
import { useSaveVideo } from '../../hooks/useSaveVideo';
import { useWatchState } from '../../providers/watch-state.provider';
import { buildVideoCardWatchUrl } from '../../utils/watch-state';
import { useAnalyticsService } from '../../services/analytics.service';
import { ANALYTICS_EVENT_TYPES } from '../../types/analytics.types';
import styles from './VideoCard.module.scss';

export type VideoCardVariant = 'grid' | 'compact';

export interface VideoCardProps {
    video: VideoCardData;
    variant?: VideoCardVariant;
    onSelect?: (video: VideoCardData) => void;
    analyticsContext?: {
        source?: string;
        query?: string;
        tags?: string[];
    };
}

// Order by corner color: blue first, red last, neutral in the middle.
/**
 * Reusable video card used across the app.
 */
const VideoCard = ({ video, variant = 'grid', onSelect, analyticsContext }: VideoCardProps) => {
    const isCompact = variant === 'compact';
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const { isVideoOwned } = usePurchaseStatus();
    const { startPurchase, isProcessing: isPurchaseProcessing } = usePurchaseFlow();
    const { toggleSaved, isSaved, isSaving } = useSaveVideo();
    const { getWatchState } = useWatchState();
    const { trackEvent } = useAnalyticsService();
    const purchaseEnabled = video.purchaseEnabled ?? true;
    const isOwned = isVideoOwned(video.id);
    const hasValidPurchaseId = /^[0-9a-fA-F-]{36}$/.test(video.id);
    const canPurchase = hasValidPurchaseId;
    const canSave = hasValidPurchaseId;
    const saved = isSaved(video.id);
    const watchState = getWatchState(video);
    const watchUrl = buildVideoCardWatchUrl(video, watchState);
    const watchStateLabel = watchState?.kind === 'in-progress' ? 'Resume' : 'Watched';
    const watchStateMeta = watchState?.kind === 'in-progress'
        ? `Resume at ${watchState.resumeLabel}`
        : watchState
            ? `Watched ${watchState.lastWatchedLabel}`
            : null;

    const orderedParticipants = orderVideoCardParticipants(video.participants ?? []);
    const leftParticipant = orderedParticipants[0];
    const rightParticipant = orderedParticipants[1];
    const leftName = formatVideoCardParticipantName(leftParticipant?.name);
    const rightName = formatVideoCardParticipantName(rightParticipant?.name);

    const eventDateLabel = formatVideoCardDate(video.eventDate);
    const fightNumberLabel = formatVideoCardFightNumber(video.fightNumber);
    const metaItems = [fightNumberLabel, eventDateLabel, video.eventName, video.venueName].filter(
        (value) => Boolean(value && value.trim())
    ) as string[];
    const metaLine = metaItems.length > 0 ? metaItems.join(' | ') : null;
    const twoLineClamp = {
        display: '-webkit-box',
        overflow: 'hidden',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2
    } as React.CSSProperties;

    useEffect(() => {
        if (!isMenuOpen) {
            return;
        }

        const handleOutsideClick = (event: MouseEvent) => {
            if (!menuRef.current || menuRef.current.contains(event.target as Node)) {
                return;
            }
            setIsMenuOpen(false);
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isMenuOpen]);

    const handlePurchase = async () => {
        if (!canPurchase) {
            return;
        }
        await startPurchase(video.id);
        setIsMenuOpen(false);
    };

    const trackVideoClick = () => {
        const source = analyticsContext?.source ?? 'video_card';
        void trackEvent({
            eventType: ANALYTICS_EVENT_TYPES.VIDEO_CARD_CLICKED,
            videoId: video.id,
            metadata: {
                source,
                title: video.title,
                query: analyticsContext?.query ?? '',
                tags: analyticsContext?.tags?.join('|') ?? ''
            }
        });

        if (source === 'search_results') {
            void trackEvent({
                eventType: ANALYTICS_EVENT_TYPES.SEARCH_RESULT_CLICKED,
                videoId: video.id,
                metadata: {
                    source,
                    title: video.title,
                    query: analyticsContext?.query ?? '',
                    tags: analyticsContext?.tags?.join('|') ?? ''
                }
            });
        }

        if (source === 'related_videos') {
            void trackEvent({
                eventType: ANALYTICS_EVENT_TYPES.RELATED_VIDEO_CLICKED,
                videoId: video.id,
                metadata: {
                    source,
                    title: video.title
                }
            });
        }
    };

    const handleSelect = () => {
        trackVideoClick();
        if (onSelect) {
            onSelect({ ...video, watchUrl });
        }
    };

    const thumbnail = (
        <div className={`${isCompact ? 'w-full shrink-0 sm:w-44' : 'w-full'} relative aspect-video overflow-hidden rounded-lg bg-slate-200`}>
            {video.thumbnailUrl ? (
                <img
                    src={`data:image/png;base64,${video.thumbnailUrl}`}
                    alt={video.title || 'Video thumbnail'}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center bg-slate-100 text-xs text-slate-500">
                    No thumbnail available
                </div>
            )}
            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${styles.playOverlay}`}>
                <div style={isCompact ? { transform: 'scale(0.4)' } : undefined}>
                    <MediaButton buttonType={MediaButtonType.PLAY} styleType={ButtonStyleType.OUTLINE} />
                </div>
            </div>
            {watchState ? (
                <span className={`absolute left-2 top-2 z-10 rounded-full px-2 py-1 text-[11px] font-bold uppercase tracking-wide shadow ${
                    watchState.kind === 'in-progress'
                        ? 'bg-sky-500 text-white'
                        : 'bg-slate-950/80 text-white'
                }`}>
                    {watchStateLabel}
                </span>
            ) : null}
            {canSave ? (
                <button
                    type="button"
                    className={`absolute bottom-2 right-2 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-transparent shadow transition sm:h-9 sm:w-9 ${
                        saved
                            ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600'
                            : 'bg-white/90 text-slate-700 hover:bg-white'
                    }`}
                    aria-label={saved ? 'Remove from saved' : 'Save for later'}
                    aria-pressed={saved}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!isSaving) {
                            void toggleSaved(video.id);
                        }
                    }}
                >
                    <span className={`material-symbols-outlined text-base ${saved ? 'text-white' : 'text-slate-700'}`}>
                        {saved ? 'bookmark' : 'bookmark_add'}
                    </span>
                </button>
            ) : null}
            {watchState ? (
                <span className="absolute inset-x-0 bottom-0 block h-1.5 bg-slate-950/40" aria-hidden="true">
                    <span
                        className={`block h-full ${watchState.kind === 'in-progress' ? 'bg-sky-400' : 'bg-emerald-400'}`}
                        style={{ width: `${watchState.progressPercent}%` }}
                    />
                </span>
            ) : null}
        </div>
    );

    return (
        <article className={`group relative h-full min-w-0 ${isCompact ? 'flex flex-col gap-3 sm:flex-row sm:gap-4' : 'flex flex-col gap-3'}`}>
            {canPurchase ? (
                <div className="absolute right-2 top-2 z-10" ref={menuRef}>
                    <button
                        type="button"
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow transition hover:bg-white sm:h-8 sm:w-8"
                        aria-label="Video actions"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setIsMenuOpen((prev) => !prev);
                        }}
                    >
                        <span className="material-symbols-outlined text-base">more_vert</span>
                    </button>
                    {isMenuOpen ? (
                        <div className="absolute right-0 mt-2 w-48 rounded-xl bg-white p-2 text-sm shadow-lg">
                            {isOwned ? (
                                <div className="px-3 py-2 text-slate-600">Owned</div>
                            ) : purchaseEnabled ? (
                                <button
                                    type="button"
                                    className="w-full rounded-lg px-3 py-2 text-left text-slate-900 hover:bg-slate-100"
                                    disabled={isPurchaseProcessing}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handlePurchase();
                                    }}
                                >
                                    {isPurchaseProcessing ? 'Starting checkout...' : 'Buy Now'}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="w-full rounded-lg px-3 py-2 text-left text-slate-900 hover:bg-slate-100"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        window.location.href = Routes.CONTACT;
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    Contact us about purchase
                                </button>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {onSelect ? (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={handleSelect}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleSelect();
                        }
                    }}
                    className="block cursor-pointer text-left"
                >
                    {thumbnail}
                </div>
            ) : (
                <Link to={watchUrl} className="block" onClick={trackVideoClick}>
                    {thumbnail}
                </Link>
            )}

            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <FighterCornerBars
                    leftName={leftName}
                    leftColor={leftParticipant?.cornerColor ?? null}
                    rightName={rightName}
                    rightColor={rightParticipant?.cornerColor ?? null}
                    layout="row"
                    size={isCompact ? 'sm' : 'md'}
                    stackOnMobile
                />

                {isOwned ? (
                    <span className="inline-flex w-fit items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                        Owned
                    </span>
                ) : null}

                {watchState ? (
                    <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        watchState.kind === 'in-progress'
                            ? 'bg-sky-50 text-sky-700'
                            : 'bg-slate-100 text-slate-700'
                    }`}>
                        {watchStateLabel}
                    </span>
                ) : null}

                {onSelect ? (
                    <button type="button" onClick={handleSelect} className="text-left text-base font-semibold leading-snug text-slate-900" style={twoLineClamp}>
                        {video.title}
                    </button>
                ) : (
                    <Link to={watchUrl} className="text-base font-semibold leading-snug text-slate-900" onClick={trackVideoClick} style={twoLineClamp}>
                        {video.title}
                    </Link>
                )}

                {metaLine ? <p className="text-xs leading-snug text-slate-500" style={twoLineClamp}>{metaLine}</p> : null}
                {watchStateMeta ? (
                    <p className="text-xs font-semibold leading-snug text-sky-700">{watchStateMeta}</p>
                ) : null}
            </div>
        </article>
    );
};

export default VideoCard;
